/**
 * Pagination Component - Enhanced Navigation Component
 *
 * A comprehensive pagination component that provides:
 * - Brand color integration with consistent theming
 * - Variant system for different contexts (table, card, compact)
 * - Loading states during page changes
 * - Enhanced accessibility and keyboard navigation
 * - Flexible customization options
 *
 * Features:
 * - Semantic variants for different contexts
 * - Brand color consistency throughout
 * - Loading states with visual feedback
 * - Enhanced accessibility features
 * - Responsive design patterns
 * - Keyboard navigation support
 *
 * Usage:
 * ```tsx
 * // Standard table pagination
 * <Pagination
 *   variant="table"
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPageChange={handlePageChange}
 *   loading={isLoading}
 * />
 *
 * // Compact pagination for cards
 * <Pagination
 *   variant="compact"
 *   currentPage={page}
 *   totalPages={total}
 *   onPageChange={setPage}
 *   animate={true}
 * />
 *
 * // Full-featured pagination
 * <Pagination
 *   variant="full"
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   totalItems={totalItems}
 *   itemsPerPage={itemsPerPage}
 *   onPageChange={handlePageChange}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 *   showItemsPerPageSelector={true}
 *   loading={isLoading}
 *   animate={true}
 * />
 * ```
 */

"use client";

import { Button } from "@/components/ui/primitives/button";
import { Select } from "@/components/ui/primitives/select";
import { cn } from "@/lib/utils/general-utils";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ChevronsLeft as ChevronDoubleLeftIcon,
  ChevronsRight as ChevronDoubleRightIcon,
} from "lucide-react";
import * as React from "react";

// Enhanced variant system for pagination
export type PaginationVariant = "table" | "card" | "compact" | "full";
export type PaginationSize = "sm" | "md" | "lg";

export interface PaginationProps {
  /** Current active page number */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items (optional) */
  totalItems?: number;
  /** Number of items per page */
  itemsPerPage?: number;
  /** Page change handler */
  onPageChange: (page: number) => void;
  /** Items per page change handler */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  /** Whether to show items per page selector */
  showItemsPerPageSelector?: boolean;
  /** Available options for items per page */
  itemsPerPageOptions?: number[];
  /** Number of sibling pages to show */
  siblingCount?: number;
  /** Additional CSS classes */
  className?: string;
  /** Pagination variant */
  variant?: PaginationVariant;
  /** Size variant */
  size?: PaginationSize;
  /** Whether pagination is in loading state */
  loading?: boolean;
  /** Whether to animate transitions */
  animate?: boolean;
  /** Whether pagination is disabled */
  disabled?: boolean;
}

// Brand color configuration
const BRAND_COLORS = {
  primary: "rgb(51, 49, 50)", // Graphene #333132
  primaryHover: "rgb(68, 66, 67)",
  primaryAlpha10: "rgb(51, 49, 50, 0.1)",
  primaryAlpha20: "rgb(51, 49, 50, 0.2)",
};

// Utility functions for variant styling
function getVariantStyles(variant: PaginationVariant, size: PaginationSize) {
  const sizes = {
    sm: {
      button: "min-w-[2rem] h-8 text-sm",
      text: "text-sm",
      gap: "gap-1",
    },
    md: {
      button: "min-w-[2.5rem] h-10 text-sm",
      text: "text-sm",
      gap: "gap-2",
    },
    lg: {
      button: "min-w-[3rem] h-12 text-base",
      text: "text-base",
      gap: "gap-3",
    },
  };

  const variants = {
    table: {
      container: "flex items-center justify-between",
      showItemsPerPage: true,
      showTotalItems: true,
    },
    card: {
      container: "flex items-center justify-center",
      showItemsPerPage: false,
      showTotalItems: false,
    },
    compact: {
      container: "flex items-center justify-center",
      showItemsPerPage: false,
      showTotalItems: false,
    },
    full: {
      container: "flex items-center justify-between",
      showItemsPerPage: true,
      showTotalItems: true,
    },
  };

  return {
    ...variants[variant],
    ...sizes[size],
  };
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPageSelector = false,
  itemsPerPageOptions = [10, 25, 50, 100],
  siblingCount = 1,
  className = "",
  variant = "table",
  size = "md",
  loading = false,
  animate: _animate = false,
  disabled = false,
}: Readonly<PaginationProps>) {
  const [activePage, setActivePage] = React.useState<number>(currentPage);

  // Sync local page when parent updates, but avoid snapping back during a fetch
  // React.useEffect(() => {
  //   if (!loading && currentPage !== activePage) {
  //     setActivePage(currentPage);
  //   }
  // }, [currentPage, loading, activePage]);
  // Get variant-based styling
  const variantStyles = getVariantStyles(variant, size);

  // Override showItemsPerPageSelector based on variant if not explicitly set
  const shouldShowItemsPerPage =
    showItemsPerPageSelector ?? variantStyles.showItemsPerPage;
  const shouldShowTotalItems = variantStyles.showTotalItems;
  // Generate page numbers to display
  const getPageNumbers = () => {
    const totalNumbers = siblingCount * 2 + 3; // siblingCount on each side + current page + first page + last page
    const totalNumbersWithDots = Math.min(totalNumbers, totalPages);

    if (totalPages <= totalNumbersWithDots) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(activePage - siblingCount, 1);
    const rightSiblingIndex = Math.min(activePage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 1 + 2 * siblingCount;
      return [...range(1, leftItemCount), "...", totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 1 + 2 * siblingCount;
      return [1, "...", ...range(totalPages - rightItemCount + 1, totalPages)];
    }

    return [
      1,
      "...",
      ...range(leftSiblingIndex, rightSiblingIndex),
      "...",
      totalPages,
    ];
  };

  // Helper to create range array
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  // Handle page change with loading state
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== activePage && !disabled) {
      setActivePage(page); // Optimistic UI update so footer reflects immediately
      onPageChange(page);
    }
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={cn(
        variantStyles.container,
        variantStyles.text,
        loading && "opacity-75 pointer-events-none",
        disabled && "opacity-50 pointer-events-none",
        className,
      )}
    >
      {/* Left: Items per page selector */}
      <div className="flex items-center gap-4">
        {shouldShowItemsPerPage && onItemsPerPageChange && itemsPerPage ? (
          <div className="flex items-center gap-2">
            <label
              htmlFor="items-per-page"
              className={cn("font-medium text-gray-700", variantStyles.text)}
            >
              Show:
            </label>
            <div className="relative">
              <Select
                value={itemsPerPage.toString()}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                disabled={loading || disabled}
                className="w-auto"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>
            <span
              className={cn("font-medium text-gray-700", variantStyles.text)}
            >
              per page
            </span>
          </div>
        ) : variant === "table" || variant === "full" ? (
          <div></div> // Empty div to maintain layout
        ) : null}
      </div>

      {/* Center: Total records */}
      {shouldShowTotalItems && totalItems !== undefined && (
        <div
          className={cn("flex items-center justify-center", variantStyles.text)}
        >
          <span className="font-medium text-gray-700">
            {loading ? (
              "Loading..."
            ) : (
              <>
                {totalItems.toLocaleString()}{" "}
                {totalItems === 1 ? "record" : "records"}
              </>
            )}
          </span>
        </div>
      )}

      {/* Right: Pagination controls */}
      <div className={cn("flex items-center", variantStyles.gap)}>
        {/* First page button - only show for non-compact variants */}
        {variant !== "compact" && (
          <div>
            <Button
              plain
              onClick={() => handlePageChange(1)}
              disabled={activePage === 1 || loading || disabled}
              aria-label="Go to first page"
              className={cn(
                variantStyles.button,
                "text-brand-800 hover:text-brand-800 hover:bg-brand-800/10",
                "data-hover:bg-brand-800/10 data-active:bg-brand-800/20",
                "disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:data-hover:bg-transparent",
                "transition-all duration-200",
              )}
            >
              <ChevronDoubleLeftIcon className="w-4 h-4" data-slot="icon" />
            </Button>
          </div>
        )}

        {/* Previous page button */}
        <div>
          <Button
            plain
            onClick={() => handlePageChange(activePage - 1)}
            disabled={activePage === 1 || loading || disabled}
            aria-label="Go to previous page"
            className={cn(
              variantStyles.button,
              "text-brand-800 hover:text-brand-800 hover:bg-brand-800/10",
              "data-hover:bg-brand-800/10 data-active:bg-brand-800/20",
              "disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:data-hover:bg-transparent",
              "transition-all duration-200",
            )}
          >
            <ChevronLeftIcon className="w-4 h-4" data-slot="icon" />
          </Button>
        </div>

        {/* Page numbers */}
        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === "...") {
            return (
              <span
                key={`ellipsis-${index}`}
                className={cn(
                  "px-3 py-1 text-text-tertiary",
                  variantStyles.text,
                )}
              >
                ...
              </span>
            );
          }

          const isCurrentPage = pageNumber === activePage;

          return (
            <div key={pageNumber}>
              {isCurrentPage ? (
                <Button
                  onClick={() => handlePageChange(pageNumber as number)}
                  aria-label={`Go to page ${pageNumber}`}
                  aria-current="page"
                  disabled={loading || disabled}
                  className={cn(
                    variantStyles.button,
                    "bg-brand-800 text-white hover:bg-brand-800/90 data-hover:bg-brand-800/90",
                    "border-brand-800 transition-all duration-200",
                    "disabled:opacity-50",
                  )}
                  style={
                    {
                      "--btn-bg": BRAND_COLORS.primary,
                      "--btn-border": BRAND_COLORS.primary,
                      "--btn-hover-overlay": "rgba(255, 255, 255, 0.1)",
                    } as React.CSSProperties
                  }
                >
                  {loading ? "..." : pageNumber}
                </Button>
              ) : (
                <div>
                  <Button
                    plain
                    onClick={() => handlePageChange(pageNumber as number)}
                    aria-label={`Go to page ${pageNumber}`}
                    disabled={loading || disabled}
                    className={cn(
                      variantStyles.button,
                      "text-brand-800 hover:text-brand-800 hover:bg-brand-800/10",
                      "data-hover:bg-brand-800/10 data-active:bg-brand-800/20",
                      "transition-all duration-200",
                      "disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:data-hover:bg-transparent",
                    )}
                  >
                    {pageNumber}
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {/* Next page button */}
        <div>
          <Button
            plain
            onClick={() => handlePageChange(activePage + 1)}
            disabled={activePage === totalPages || loading || disabled}
            aria-label="Go to next page"
            className={cn(
              variantStyles.button,
              "text-brand-800 hover:text-brand-800 hover:bg-brand-800/10",
              "data-hover:bg-brand-800/10 data-active:bg-brand-800/20",
              "disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:data-hover:bg-transparent",
              "transition-all duration-200",
            )}
          >
            <ChevronRightIcon className="w-4 h-4" data-slot="icon" />
          </Button>
        </div>

        {/* Last page button - only show for non-compact variants */}
        {variant !== "compact" && (
          <div>
            <Button
              plain
              onClick={() => handlePageChange(totalPages)}
              disabled={activePage === totalPages || loading || disabled}
              aria-label="Go to last page"
              className={cn(
                variantStyles.button,
                "text-brand-800 hover:text-brand-800 hover:bg-brand-800/10",
                "data-hover:bg-brand-800/10 data-active:bg-brand-800/20",
                "disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:data-hover:bg-transparent",
                "transition-all duration-200",
              )}
            >
              <ChevronDoubleRightIcon className="w-4 h-4" data-slot="icon" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
